import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import ElevatedButton from '../Layout/ElevatedButton';
import TwoFactorSetup from '../Auth/TwoFactorSetup';
import TwoFactorDisableModal from '../Auth/TwoFactorDisableModal';

const Container = styled.div`
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${props => props.compact ? '0.125rem' : '1rem'};
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.text};
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${props => props.theme.border};
`;

const SettingCard = styled.div`
  padding: ${props => props.compact ? '0.25rem' : '1.25rem'};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.cardBackground};
  box-shadow: 0 1px 3px ${props => props.theme.shadow};
  margin-bottom: ${props => props.compact ? '0.25rem' : '1rem'};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.cardBackgroundHover};
    box-shadow: 0 2px 6px ${props => props.theme.shadowMedium};
  }
`;

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.last ? '0' : '1rem'};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const SettingInfo = styled.div`
  flex: 1;
  margin-right: 1rem;

  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 0.5rem;
    width: 100%;
  }
`;

const SettingLabel = styled.div`
  color: ${props => props.theme.text};
  font-weight: 600;
  font-size: 0.9375rem;
  margin-bottom: 0.25rem;
`;

const SettingDescription = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.8125rem;
  line-height: 1.4;
`;

const SmallLabel = styled.label`
  font-size: 0.75rem;
  color: ${props => props.theme.textMuted};
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  font-size: 0.875rem;
  min-width: 150px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.inputBorderFocus};
    background: ${props => props.theme.inputBackgroundFocus};
  }

  &:hover {
    border-color: ${props => props.theme.inputBorderFocus};
  }
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  font-size: 0.875rem;
  min-width: 150px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.inputBorderFocus};
    background: ${props => props.theme.inputBackgroundFocus};
  }

  &:hover {
    border-color: ${props => props.theme.inputBorderFocus};
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: #5865f2;
  }

  &:checked + span:before {
    transform: translateX(26px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props => props.theme.border};
  transition: 0.3s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }

  &:hover {
    background-color: ${props => props.theme.buttonSecondaryHover};
  }
`;

const SaveButton = styled(ElevatedButton)`
  background: #28a745;
  border: 1px solid #28a745;

  &:hover {
    background: #218838;
    border: 1px solid #218838;
  }
`;

const SaveIndicator = styled.div`
  display: inline-block;
  margin-left: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  background: ${props => props.success ? '#28a745' : props.error ? '#dc3545' : '#fbc02d'};
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const GPAInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-start;
  padding: 0.75rem 0;

  @media (min-width: 769px) {
    gap: 1rem;
  }
`;

const SettingsForm = () => {
  const { settings, updateSettings } = useData();
  const { user } = useAuth();
  const [localSettings, setLocalSettings] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [show2FADisable, setShow2FADisable] = useState(false);
  const [twoFactorStatus, setTwoFactorStatus] = useState({
    enabled: false,
    loading: false
  });

  // Compact mode is a user setting, available to all users
  const isCompact = settings?.display?.compactMode;

  useEffect(() => {
    if (settings && !localSettings) {
      setLocalSettings(settings);
    }
  }, [settings, localSettings]);

  // Sync 2FA status with user object changes
  useEffect(() => {
    setTwoFactorStatus(prev => ({
      ...prev,
      enabled: user?.twoFactorEnabled || false
    }));
  }, [user?.twoFactorEnabled]);

  // Don't render until settings are loaded
  if (!localSettings) {
    return null;
  }

  const handleChange = (category, key, value) => {
    setLocalSettings(prev => {
      // Safety check
      if (!prev) return prev;
      
      // Handle top-level settings (like aiModelPreference)
      if (!category && key) {
        return {
          ...prev,
          [key]: value
        };
      }
      
      // Handle nested settings
      if (!prev[category]) return prev;
      
      return {
        ...prev,
        [category]: {
          ...prev[category],
          [key]: value
        }
      };
    });
  };

  const handleGPAChange = (grade, field, value) => {
    setLocalSettings(prev => {
      // Safety check
      if (!prev || !prev.gpa || !prev.gpa[grade]) return prev;
      
      return {
        ...prev,
        gpa: {
          ...prev.gpa,
          [grade]: {
            ...prev.gpa[grade],
            [field]: value
          }
        }
      };
    });
  };

  const handleSave = async () => {
    try {
      await updateSettings(localSettings);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 5000);
    }
  };

  const handle2FAEnable = () => {
    setShow2FASetup(true);
  };

  const handle2FADisable = async () => {
    setShow2FADisable(true);
  };

  const handle2FAComplete = () => {
    setShow2FASetup(false);
    setTwoFactorStatus({ enabled: true, loading: false });
  };

  const handle2FADisableSuccess = (message) => {
    setShow2FADisable(false);
    setTwoFactorStatus({ enabled: false, loading: false });
    // Show success message
    alert(message);
  };

  const handle2FACancel = () => {
    setShow2FASetup(false);
  };

  const handle2FADisableCancel = () => {
    setShow2FADisable(false);
  };

  if (show2FASetup) {
    return <TwoFactorSetup onComplete={handle2FAComplete} onCancel={handle2FACancel} />;
  }

  if (show2FADisable) {
    return <TwoFactorDisableModal onSuccess={handle2FADisableSuccess} onCancel={handle2FADisableCancel} />;
  }

  // Don't render if settings haven't loaded yet
  if (!localSettings || !localSettings.notifications || !localSettings.preferences || !localSettings.display || !localSettings.gpa) {
    return <Container compact={isCompact}><div style={{ color: 'var(--color-text-muted)', padding: '2rem', textAlign: 'center' }}>Loading settings...</div></Container>;
  }

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(localSettings);

  return (
    <Container compact={isCompact}>
      <Section>
        <SectionTitle>Security</SectionTitle>
        <SettingCard compact={isCompact}>
          <SettingRow>
            <SettingInfo>
              <SettingLabel>Two-Factor Authentication</SettingLabel>
              <SettingDescription>Adds an extra layer of security and allows password resetting on your account</SettingDescription>
            </SettingInfo>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ 
                color: twoFactorStatus.enabled ? '#28a745' : '#dc3545', 
                fontWeight: '600',
                fontSize: '0.875rem'
              }}>
                {twoFactorStatus.enabled ? 'ENABLED' : 'DISABLED'}
              </span>
              <ElevatedButton
                onClick={twoFactorStatus.enabled ? handle2FADisable : handle2FAEnable}
                disabled={twoFactorStatus.loading}
                style={{
                  background: twoFactorStatus.enabled ? '#dc3545' : '#28a745',
                  border: `1px solid ${twoFactorStatus.enabled ? '#dc3545' : '#28a745'}`,
                  fontSize: '0.875rem',
                  padding: '0.5rem 1rem'
                }}
              >
                {twoFactorStatus.loading ? 'Loading...' : (twoFactorStatus.enabled ? 'Disable 2FA' : 'Enable 2FA')}
              </ElevatedButton>
            </div>
          </SettingRow>
        </SettingCard>
      </Section>

      <Section>
        <SectionTitle>Notifications</SectionTitle>
        <SettingCard compact={isCompact}>
          <SettingRow>
            <SettingInfo>
              <SettingLabel>Push Notifications</SettingLabel>
              <SettingDescription>Receive browser push notifications</SettingDescription>
            </SettingInfo>
            <ToggleSwitch>
              <ToggleInput
                type="checkbox"
                checked={localSettings.notifications.push}
                onChange={(e) => handleChange('notifications', 'push', e.target.checked)}
              />
              <ToggleSlider />
            </ToggleSwitch>
          </SettingRow>
          <SettingRow>
            <SettingInfo>
              <SettingLabel>Due Date Reminders</SettingLabel>
              <SettingDescription>Get notified about upcoming assignment due dates</SettingDescription>
            </SettingInfo>
            <ToggleSwitch>
              <ToggleInput
                type="checkbox"
                checked={localSettings.notifications.dueDateReminders}
                onChange={(e) => handleChange('notifications', 'dueDateReminders', e.target.checked)}
              />
              <ToggleSlider />
            </ToggleSwitch>
          </SettingRow>
          <SettingRow last>
            <SettingInfo>
              <SettingLabel>Reminder Time</SettingLabel>
              <SettingDescription>Hours before due date to send reminder</SettingDescription>
            </SettingInfo>
            <Input
              type="number"
              min="1"
              max="168"
              value={localSettings.notifications.reminderTime}
              onChange={(e) => handleChange('notifications', 'reminderTime', parseInt(e.target.value))}
            />
          </SettingRow>
        </SettingCard>
      </Section>

      <Section>
        <SectionTitle>Preferences</SectionTitle>
        <SettingCard compact={isCompact}>
          <SettingRow>
            <SettingInfo>
              <SettingLabel>Timezone</SettingLabel>
              <SettingDescription>Your local timezone</SettingDescription>
            </SettingInfo>
            <Select
              value={localSettings.preferences.timezone}
              onChange={(e) => handleChange('preferences', 'timezone', e.target.value)}
            >
            <option value="UTC">UTC (GMT+0)</option>
            
            <optgroup label="Africa">
              <option value="Africa/Abidjan">Abidjan (GMT+0)</option>
              <option value="Africa/Accra">Accra (GMT+0)</option>
              <option value="Africa/Addis_Ababa">Addis Ababa (GMT+3)</option>
              <option value="Africa/Algiers">Algiers (GMT+1)</option>
              <option value="Africa/Asmara">Asmara (GMT+3)</option>
              <option value="Africa/Bamako">Bamako (GMT+0)</option>
              <option value="Africa/Bangui">Bangui (GMT+1)</option>
              <option value="Africa/Banjul">Banjul (GMT+0)</option>
              <option value="Africa/Bissau">Bissau (GMT+0)</option>
              <option value="Africa/Blantyre">Blantyre (GMT+2)</option>
              <option value="Africa/Brazzaville">Brazzaville (GMT+1)</option>
              <option value="Africa/Bujumbura">Bujumbura (GMT+2)</option>
              <option value="Africa/Cairo">Cairo (GMT+2)</option>
              <option value="Africa/Casablanca">Casablanca (GMT+1)</option>
              <option value="Africa/Ceuta">Ceuta (GMT+1)</option>
              <option value="Africa/Conakry">Conakry (GMT+0)</option>
              <option value="Africa/Dakar">Dakar (GMT+0)</option>
              <option value="Africa/Dar_es_Salaam">Dar es Salaam (GMT+3)</option>
              <option value="Africa/Djibouti">Djibouti (GMT+3)</option>
              <option value="Africa/Douala">Douala (GMT+1)</option>
              <option value="Africa/El_Aaiun">El Aaiun (GMT+1)</option>
              <option value="Africa/Freetown">Freetown (GMT+0)</option>
              <option value="Africa/Gaborone">Gaborone (GMT+2)</option>
              <option value="Africa/Harare">Harare (GMT+2)</option>
              <option value="Africa/Johannesburg">Johannesburg (GMT+2)</option>
              <option value="Africa/Juba">Juba (GMT+2)</option>
              <option value="Africa/Kampala">Kampala (GMT+3)</option>
              <option value="Africa/Khartoum">Khartoum (GMT+2)</option>
              <option value="Africa/Kigali">Kigali (GMT+2)</option>
              <option value="Africa/Kinshasa">Kinshasa (GMT+1)</option>
              <option value="Africa/Lagos">Lagos (GMT+1)</option>
              <option value="Africa/Libreville">Libreville (GMT+1)</option>
              <option value="Africa/Lome">Lome (GMT+0)</option>
              <option value="Africa/Luanda">Luanda (GMT+1)</option>
              <option value="Africa/Lubumbashi">Lubumbashi (GMT+2)</option>
              <option value="Africa/Lusaka">Lusaka (GMT+2)</option>
              <option value="Africa/Malabo">Malabo (GMT+1)</option>
              <option value="Africa/Maputo">Maputo (GMT+2)</option>
              <option value="Africa/Maseru">Maseru (GMT+2)</option>
              <option value="Africa/Mbabane">Mbabane (GMT+2)</option>
              <option value="Africa/Mogadishu">Mogadishu (GMT+3)</option>
              <option value="Africa/Monrovia">Monrovia (GMT+0)</option>
              <option value="Africa/Nairobi">Nairobi (GMT+3)</option>
              <option value="Africa/Ndjamena">Ndjamena (GMT+1)</option>
              <option value="Africa/Niamey">Niamey (GMT+1)</option>
              <option value="Africa/Nouakchott">Nouakchott (GMT+0)</option>
              <option value="Africa/Ouagadougou">Ouagadougou (GMT+0)</option>
              <option value="Africa/Porto-Novo">Porto-Novo (GMT+1)</option>
              <option value="Africa/Sao_Tome">Sao Tome (GMT+0)</option>
              <option value="Africa/Tripoli">Tripoli (GMT+2)</option>
              <option value="Africa/Tunis">Tunis (GMT+1)</option>
              <option value="Africa/Windhoek">Windhoek (GMT+2)</option>
            </optgroup>
            
            <optgroup label="America">
              <option value="America/Adak">Adak (GMT-10)</option>
              <option value="America/Anchorage">Anchorage (GMT-9)</option>
              <option value="America/Anguilla">Anguilla (GMT-4)</option>
              <option value="America/Antigua">Antigua (GMT-4)</option>
              <option value="America/Araguaina">Araguaina (GMT-3)</option>
              <option value="America/Argentina/Buenos_Aires">Buenos Aires (GMT-3)</option>
              <option value="America/Argentina/Catamarca">Catamarca (GMT-3)</option>
              <option value="America/Argentina/Cordoba">Cordoba (GMT-3)</option>
              <option value="America/Argentina/Jujuy">Jujuy (GMT-3)</option>
              <option value="America/Argentina/La_Rioja">La Rioja (GMT-3)</option>
              <option value="America/Argentina/Mendoza">Mendoza (GMT-3)</option>
              <option value="America/Argentina/Rio_Gallegos">Rio Gallegos (GMT-3)</option>
              <option value="America/Argentina/Salta">Salta (GMT-3)</option>
              <option value="America/Argentina/San_Juan">San Juan (GMT-3)</option>
              <option value="America/Argentina/San_Luis">San Luis (GMT-3)</option>
              <option value="America/Argentina/Tucuman">Tucuman (GMT-3)</option>
              <option value="America/Argentina/Ushuaia">Ushuaia (GMT-3)</option>
              <option value="America/Aruba">Aruba (GMT-4)</option>
              <option value="America/Asuncion">Asuncion (GMT-4)</option>
              <option value="America/Atikokan">Atikokan (GMT-5)</option>
              <option value="America/Bahia">Bahia (GMT-3)</option>
              <option value="America/Bahia_Banderas">Bahia Banderas (GMT-6)</option>
              <option value="America/Barbados">Barbados (GMT-4)</option>
              <option value="America/Belem">Belem (GMT-3)</option>
              <option value="America/Belize">Belize (GMT-6)</option>
              <option value="America/Blanc-Sablon">Blanc-Sablon (GMT-4)</option>
              <option value="America/Boa_Vista">Boa Vista (GMT-4)</option>
              <option value="America/Bogota">Bogota (GMT-5)</option>
              <option value="America/Boise">Boise (GMT-7)</option>
              <option value="America/Cambridge_Bay">Cambridge Bay (GMT-7)</option>
              <option value="America/Campo_Grande">Campo Grande (GMT-4)</option>
              <option value="America/Cancun">Cancun (GMT-5)</option>
              <option value="America/Caracas">Caracas (GMT-4)</option>
              <option value="America/Cayenne">Cayenne (GMT-3)</option>
              <option value="America/Cayman">Cayman (GMT-5)</option>
              <option value="America/Chicago">Chicago (GMT-6)</option>
              <option value="America/Chihuahua">Chihuahua (GMT-7)</option>
              <option value="America/Costa_Rica">Costa Rica (GMT-6)</option>
              <option value="America/Creston">Creston (GMT-7)</option>
              <option value="America/Cuiaba">Cuiaba (GMT-4)</option>
              <option value="America/Curacao">Curacao (GMT-4)</option>
              <option value="America/Danmarkshavn">Danmarkshavn (GMT+0)</option>
              <option value="America/Dawson">Dawson (GMT-8)</option>
              <option value="America/Dawson_Creek">Dawson Creek (GMT-7)</option>
              <option value="America/Denver">Denver (GMT-7)</option>
              <option value="America/Detroit">Detroit (GMT-5)</option>
              <option value="America/Dominica">Dominica (GMT-4)</option>
              <option value="America/Edmonton">Edmonton (GMT-7)</option>
              <option value="America/Eirunepe">Eirunepe (GMT-5)</option>
              <option value="America/El_Salvador">El Salvador (GMT-6)</option>
              <option value="America/Fort_Nelson">Fort Nelson (GMT-7)</option>
              <option value="America/Fortaleza">Fortaleza (GMT-3)</option>
              <option value="America/Glace_Bay">Glace Bay (GMT-4)</option>
              <option value="America/Godthab">Godthab (GMT-3)</option>
              <option value="America/Goose_Bay">Goose Bay (GMT-4)</option>
              <option value="America/Grand_Turk">Grand Turk (GMT-5)</option>
              <option value="America/Grenada">Grenada (GMT-4)</option>
              <option value="America/Guadeloupe">Guadeloupe (GMT-4)</option>
              <option value="America/Guatemala">Guatemala (GMT-6)</option>
              <option value="America/Guayaquil">Guayaquil (GMT-5)</option>
              <option value="America/Guyana">Guyana (GMT-4)</option>
              <option value="America/Halifax">Halifax (GMT-4)</option>
              <option value="America/Havana">Havana (GMT-5)</option>
              <option value="America/Hermosillo">Hermosillo (GMT-7)</option>
              <option value="America/Indiana/Indianapolis">Indianapolis (GMT-5)</option>
              <option value="America/Indiana/Knox">Knox (GMT-6)</option>
              <option value="America/Indiana/Marengo">Marengo (GMT-5)</option>
              <option value="America/Indiana/Petersburg">Petersburg (GMT-5)</option>
              <option value="America/Indiana/Tell_City">Tell City (GMT-6)</option>
              <option value="America/Indiana/Vevay">Vevay (GMT-5)</option>
              <option value="America/Indiana/Vincennes">Vincennes (GMT-5)</option>
              <option value="America/Indiana/Winamac">Winamac (GMT-5)</option>
              <option value="America/Inuvik">Inuvik (GMT-7)</option>
              <option value="America/Iqaluit">Iqaluit (GMT-5)</option>
              <option value="America/Jamaica">Jamaica (GMT-5)</option>
              <option value="America/Juneau">Juneau (GMT-9)</option>
              <option value="America/Kentucky/Louisville">Louisville (GMT-5)</option>
              <option value="America/Kentucky/Monticello">Monticello (GMT-5)</option>
              <option value="America/Kralendijk">Kralendijk (GMT-4)</option>
              <option value="America/La_Paz">La Paz (GMT-4)</option>
              <option value="America/Lima">Lima (GMT-5)</option>
              <option value="America/Los_Angeles">Los Angeles (GMT-8)</option>
              <option value="America/Lower_Princes">Lower Princes (GMT-4)</option>
              <option value="America/Maceio">Maceio (GMT-3)</option>
              <option value="America/Managua">Managua (GMT-6)</option>
              <option value="America/Manaus">Manaus (GMT-4)</option>
              <option value="America/Marigot">Marigot (GMT-4)</option>
              <option value="America/Martinique">Martinique (GMT-4)</option>
              <option value="America/Matamoros">Matamoros (GMT-6)</option>
              <option value="America/Mazatlan">Mazatlan (GMT-7)</option>
              <option value="America/Menominee">Menominee (GMT-6)</option>
              <option value="America/Merida">Merida (GMT-6)</option>
              <option value="America/Metlakatla">Metlakatla (GMT-9)</option>
              <option value="America/Mexico_City">Mexico City (GMT-6)</option>
              <option value="America/Miquelon">Miquelon (GMT-3)</option>
              <option value="America/Moncton">Moncton (GMT-4)</option>
              <option value="America/Monterrey">Monterrey (GMT-6)</option>
              <option value="America/Montevideo">Montevideo (GMT-3)</option>
              <option value="America/Montreal">Montreal (GMT-5)</option>
              <option value="America/Montserrat">Montserrat (GMT-4)</option>
              <option value="America/Nassau">Nassau (GMT-5)</option>
              <option value="America/New_York">New York (GMT-5)</option>
              <option value="America/Nipigon">Nipigon (GMT-5)</option>
              <option value="America/Nome">Nome (GMT-9)</option>
              <option value="America/Noronha">Noronha (GMT-2)</option>
              <option value="America/North_Dakota/Beulah">Beulah (GMT-6)</option>
              <option value="America/North_Dakota/Center">Center (GMT-6)</option>
              <option value="America/North_Dakota/New_Salem">New Salem (GMT-6)</option>
              <option value="America/Nuuk">Nuuk (GMT-3)</option>
              <option value="America/Ojinaga">Ojinaga (GMT-7)</option>
              <option value="America/Panama">Panama (GMT-5)</option>
              <option value="America/Pangnirtung">Pangnirtung (GMT-5)</option>
              <option value="America/Paramaribo">Paramaribo (GMT-3)</option>
              <option value="America/Phoenix">Phoenix (GMT-7)</option>
              <option value="America/Port-au-Prince">Port-au-Prince (GMT-5)</option>
              <option value="America/Port_of_Spain">Port of Spain (GMT-4)</option>
              <option value="America/Porto_Velho">Porto Velho (GMT-4)</option>
              <option value="America/Puerto_Rico">Puerto Rico (GMT-4)</option>
              <option value="America/Punta_Arenas">Punta Arenas (GMT-3)</option>
              <option value="America/Rainy_River">Rainy River (GMT-6)</option>
              <option value="America/Rankin_Inlet">Rankin Inlet (GMT-6)</option>
              <option value="America/Recife">Recife (GMT-3)</option>
              <option value="America/Regina">Regina (GMT-6)</option>
              <option value="America/Resolute">Resolute (GMT-6)</option>
              <option value="America/Rio_Branco">Rio Branco (GMT-5)</option>
              <option value="America/Santa_Isabel">Santa Isabel (GMT-8)</option>
              <option value="America/Santarem">Santarem (GMT-3)</option>
              <option value="America/Santiago">Santiago (GMT-4)</option>
              <option value="America/Santo_Domingo">Santo Domingo (GMT-4)</option>
              <option value="America/Sao_Paulo">Sao Paulo (GMT-3)</option>
              <option value="America/Scoresbysund">Scoresbysund (GMT-1)</option>
              <option value="America/Shiprock">Shiprock (GMT-7)</option>
              <option value="America/Sitka">Sitka (GMT-9)</option>
              <option value="America/St_Barthelemy">St Barthelemy (GMT-4)</option>
              <option value="America/St_Johns">St Johns (GMT-3.5)</option>
              <option value="America/St_Kitts">St Kitts (GMT-4)</option>
              <option value="America/St_Lucia">St Lucia (GMT-4)</option>
              <option value="America/St_Thomas">St Thomas (GMT-4)</option>
              <option value="America/St_Vincent">St Vincent (GMT-4)</option>
              <option value="America/Swift_Current">Swift Current (GMT-6)</option>
              <option value="America/Tegucigalpa">Tegucigalpa (GMT-6)</option>
              <option value="America/Thule">Thule (GMT-4)</option>
              <option value="America/Thunder_Bay">Thunder Bay (GMT-5)</option>
              <option value="America/Tijuana">Tijuana (GMT-8)</option>
              <option value="America/Toronto">Toronto (GMT-5)</option>
              <option value="America/Tortola">Tortola (GMT-4)</option>
              <option value="America/Vancouver">Vancouver (GMT-8)</option>
              <option value="America/Whitehorse">Whitehorse (GMT-8)</option>
              <option value="America/Winnipeg">Winnipeg (GMT-6)</option>
              <option value="America/Yakutat">Yakutat (GMT-9)</option>
              <option value="America/Yellowknife">Yellowknife (GMT-7)</option>
            </optgroup>
            
            <optgroup label="Antarctica">
              <option value="Antarctica/Casey">Casey (GMT+11)</option>
              <option value="Antarctica/Davis">Davis (GMT+7)</option>
              <option value="Antarctica/DumontDUrville">DumontDUrville (GMT+10)</option>
              <option value="Antarctica/Macquarie">Macquarie (GMT+11)</option>
              <option value="Antarctica/Mawson">Mawson (GMT+5)</option>
              <option value="Antarctica/McMurdo">McMurdo (GMT+12)</option>
              <option value="Antarctica/Palmer">Palmer (GMT-3)</option>
              <option value="Antarctica/Rothera">Rothera (GMT-3)</option>
              <option value="Antarctica/Syowa">Syowa (GMT+3)</option>
              <option value="Antarctica/Troll">Troll (GMT+0)</option>
              <option value="Antarctica/Vostok">Vostok (GMT+6)</option>
            </optgroup>
            
            <optgroup label="Arctic">
              <option value="Arctic/Longyearbyen">Longyearbyen (GMT+1)</option>
            </optgroup>
            
            <optgroup label="Asia">
              <option value="Asia/Aden">Aden (GMT+3)</option>
              <option value="Asia/Almaty">Almaty (GMT+6)</option>
              <option value="Asia/Amman">Amman (GMT+3)</option>
              <option value="Asia/Anadyr">Anadyr (GMT+12)</option>
              <option value="Asia/Aqtau">Aqtau (GMT+5)</option>
              <option value="Asia/Aqtobe">Aqtobe (GMT+5)</option>
              <option value="Asia/Ashgabat">Ashgabat (GMT+5)</option>
              <option value="Asia/Atyrau">Atyrau (GMT+5)</option>
              <option value="Asia/Baghdad">Baghdad (GMT+3)</option>
              <option value="Asia/Bahrain">Bahrain (GMT+3)</option>
              <option value="Asia/Baku">Baku (GMT+4)</option>
              <option value="Asia/Bangkok">Bangkok (GMT+7)</option>
              <option value="Asia/Barnaul">Barnaul (GMT+7)</option>
              <option value="Asia/Beirut">Beirut (GMT+3)</option>
              <option value="Asia/Bishkek">Bishkek (GMT+6)</option>
              <option value="Asia/Brunei">Brunei (GMT+8)</option>
              <option value="Asia/Chita">Chita (GMT+9)</option>
              <option value="Asia/Choibalsan">Choibalsan (GMT+8)</option>
              <option value="Asia/Chongqing">Chongqing (GMT+8)</option>
              <option value="Asia/Colombo">Colombo (GMT+5.5)</option>
              <option value="Asia/Damascus">Damascus (GMT+3)</option>
              <option value="Asia/Dhaka">Dhaka (GMT+6)</option>
              <option value="Asia/Dili">Dili (GMT+9)</option>
              <option value="Asia/Dubai">Dubai (GMT+4)</option>
              <option value="Asia/Dushanbe">Dushanbe (GMT+5)</option>
              <option value="Asia/Famagusta">Famagusta (GMT+3)</option>
              <option value="Asia/Gaza">Gaza (GMT+3)</option>
              <option value="Asia/Harbin">Harbin (GMT+8)</option>
              <option value="Asia/Hebron">Hebron (GMT+3)</option>
              <option value="Asia/Ho_Chi_Minh">Ho Chi Minh (GMT+7)</option>
              <option value="Asia/Hong_Kong">Hong Kong (GMT+8)</option>
              <option value="Asia/Hovd">Hovd (GMT+7)</option>
              <option value="Asia/Irkutsk">Irkutsk (GMT+8)</option>
              <option value="Asia/Jakarta">Jakarta (GMT+7)</option>
              <option value="Asia/Jayapura">Jayapura (GMT+9)</option>
              <option value="Asia/Jerusalem">Jerusalem (GMT+3)</option>
              <option value="Asia/Kabul">Kabul (GMT+4.5)</option>
              <option value="Asia/Kamchatka">Kamchatka (GMT+12)</option>
              <option value="Asia/Karachi">Karachi (GMT+5)</option>
              <option value="Asia/Kashgar">Kashgar (GMT+6)</option>
              <option value="Asia/Kathmandu">Kathmandu (GMT+5.75)</option>
              <option value="Asia/Khandyga">Khandyga (GMT+9)</option>
              <option value="Asia/Kolkata">Kolkata (GMT+5.5)</option>
              <option value="Asia/Krasnoyarsk">Krasnoyarsk (GMT+7)</option>
              <option value="Asia/Kuala_Lumpur">Kuala Lumpur (GMT+8)</option>
              <option value="Asia/Kuching">Kuching (GMT+8)</option>
              <option value="Asia/Kuwait">Kuwait (GMT+3)</option>
              <option value="Asia/Macau">Macau (GMT+8)</option>
              <option value="Asia/Magadan">Magadan (GMT+11)</option>
              <option value="Asia/Makassar">Makassar (GMT+8)</option>
              <option value="Asia/Manila">Manila (GMT+8)</option>
              <option value="Asia/Muscat">Muscat (GMT+4)</option>
              <option value="Asia/Nicosia">Nicosia (GMT+3)</option>
              <option value="Asia/Novokuznetsk">Novokuznetsk (GMT+7)</option>
              <option value="Asia/Novosibirsk">Novosibirsk (GMT+7)</option>
              <option value="Asia/Omsk">Omsk (GMT+6)</option>
              <option value="Asia/Oral">Oral (GMT+5)</option>
              <option value="Asia/Phnom_Penh">Phnom Penh (GMT+7)</option>
              <option value="Asia/Pontianak">Pontianak (GMT+7)</option>
              <option value="Asia/Pyongyang">Pyongyang (GMT+9)</option>
              <option value="Asia/Qatar">Qatar (GMT+3)</option>
              <option value="Asia/Qyzylorda">Qyzylorda (GMT+6)</option>
              <option value="Asia/Rangoon">Rangoon (GMT+6.5)</option>
              <option value="Asia/Riyadh">Riyadh (GMT+3)</option>
              <option value="Asia/Sakhalin">Sakhalin (GMT+11)</option>
              <option value="Asia/Samarkand">Samarkand (GMT+5)</option>
              <option value="Asia/Seoul">Seoul (GMT+9)</option>
              <option value="Asia/Shanghai">Shanghai (GMT+8)</option>
              <option value="Asia/Singapore">Singapore (GMT+8)</option>
              <option value="Asia/Srednekolymsk">Srednekolymsk (GMT+11)</option>
              <option value="Asia/Taipei">Taipei (GMT+8)</option>
              <option value="Asia/Tashkent">Tashkent (GMT+5)</option>
              <option value="Asia/Tbilisi">Tbilisi (GMT+4)</option>
              <option value="Asia/Tehran">Tehran (GMT+3.5)</option>
              <option value="Asia/Thimphu">Thimphu (GMT+6)</option>
              <option value="Asia/Tokyo">Tokyo (GMT+9)</option>
              <option value="Asia/Tomsk">Tomsk (GMT+7)</option>
              <option value="Asia/Ulaanbaatar">Ulaanbaatar (GMT+8)</option>
              <option value="Asia/Urumqi">Urumqi (GMT+6)</option>
              <option value="Asia/Ust-Nera">Ust-Nera (GMT+10)</option>
              <option value="Asia/Vientiane">Vientiane (GMT+7)</option>
              <option value="Asia/Vladivostok">Vladivostok (GMT+10)</option>
              <option value="Asia/Yakutsk">Yakutsk (GMT+9)</option>
              <option value="Asia/Yangon">Yangon (GMT+6.5)</option>
              <option value="Asia/Yekaterinburg">Yekaterinburg (GMT+5)</option>
              <option value="Asia/Yerevan">Yerevan (GMT+4)</option>
            </optgroup>
            
            <optgroup label="Atlantic">
              <option value="Atlantic/Azores">Azores (GMT-1)</option>
              <option value="Atlantic/Bermuda">Bermuda (GMT-4)</option>
              <option value="Atlantic/Canary">Canary (GMT+0)</option>
              <option value="Atlantic/Cape_Verde">Cape Verde (GMT-1)</option>
              <option value="Atlantic/Faroe">Faroe (GMT+0)</option>
              <option value="Atlantic/Madeira">Madeira (GMT+0)</option>
              <option value="Atlantic/Reykjavik">Reykjavik (GMT+0)</option>
              <option value="Atlantic/South_Georgia">South Georgia (GMT-2)</option>
              <option value="Atlantic/St_Helena">St Helena (GMT+0)</option>
              <option value="Atlantic/Stanley">Stanley (GMT-3)</option>
            </optgroup>
            
            <optgroup label="Australia">
              <option value="Australia/Adelaide">Adelaide (GMT+9.5)</option>
              <option value="Australia/Brisbane">Brisbane (GMT+10)</option>
              <option value="Australia/Broken_Hill">Broken Hill (GMT+9.5)</option>
              <option value="Australia/Currie">Currie (GMT+10)</option>
              <option value="Australia/Darwin">Darwin (GMT+9.5)</option>
              <option value="Australia/Eucla">Eucla (GMT+8.75)</option>
              <option value="Australia/Hobart">Hobart (GMT+10)</option>
              <option value="Australia/Lindeman">Lindeman (GMT+10)</option>
              <option value="Australia/Lord_Howe">Lord Howe (GMT+10.5)</option>
              <option value="Australia/Melbourne">Melbourne (GMT+10)</option>
              <option value="Australia/Perth">Perth (GMT+8)</option>
              <option value="Australia/Sydney">Sydney (GMT+10)</option>
            </optgroup>
            
            <optgroup label="Europe">
              <option value="Europe/Amsterdam">Amsterdam (GMT+1)</option>
              <option value="Europe/Andorra">Andorra (GMT+1)</option>
              <option value="Europe/Astrakhan">Astrakhan (GMT+4)</option>
              <option value="Europe/Athens">Athens (GMT+2)</option>
              <option value="Europe/Belgrade">Belgrade (GMT+1)</option>
              <option value="Europe/Berlin">Berlin (GMT+1)</option>
              <option value="Europe/Bratislava">Bratislava (GMT+1)</option>
              <option value="Europe/Brussels">Brussels (GMT+1)</option>
              <option value="Europe/Bucharest">Bucharest (GMT+2)</option>
              <option value="Europe/Budapest">Budapest (GMT+1)</option>
              <option value="Europe/Busingen">Busingen (GMT+1)</option>
              <option value="Europe/Chisinau">Chisinau (GMT+2)</option>
              <option value="Europe/Copenhagen">Copenhagen (GMT+1)</option>
              <option value="Europe/Dublin">Dublin (GMT+0)</option>
              <option value="Europe/Gibraltar">Gibraltar (GMT+1)</option>
              <option value="Europe/Guernsey">Guernsey (GMT+0)</option>
              <option value="Europe/Helsinki">Helsinki (GMT+2)</option>
              <option value="Europe/Isle_of_Man">Isle of Man (GMT+0)</option>
              <option value="Europe/Istanbul">Istanbul (GMT+3)</option>
              <option value="Europe/Jersey">Jersey (GMT+0)</option>
              <option value="Europe/Kaliningrad">Kaliningrad (GMT+2)</option>
              <option value="Europe/Kiev">Kiev (GMT+2)</option>
              <option value="Europe/Kirov">Kirov (GMT+3)</option>
              <option value="Europe/Lisbon">Lisbon (GMT+0)</option>
              <option value="Europe/Ljubljana">Ljubljana (GMT+1)</option>
              <option value="Europe/London">London (GMT+0)</option>
              <option value="Europe/Luxembourg">Luxembourg (GMT+1)</option>
              <option value="Europe/Madrid">Madrid (GMT+1)</option>
              <option value="Europe/Malta">Malta (GMT+1)</option>
              <option value="Europe/Mariehamn">Mariehamn (GMT+2)</option>
              <option value="Europe/Minsk">Minsk (GMT+3)</option>
              <option value="Europe/Monaco">Monaco (GMT+1)</option>
              <option value="Europe/Moscow">Moscow (GMT+3)</option>
              <option value="Europe/Oslo">Oslo (GMT+1)</option>
              <option value="Europe/Paris">Paris (GMT+1)</option>
              <option value="Europe/Podgorica">Podgorica (GMT+1)</option>
              <option value="Europe/Prague">Prague (GMT+1)</option>
              <option value="Europe/Riga">Riga (GMT+2)</option>
              <option value="Europe/Rome">Rome (GMT+1)</option>
              <option value="Europe/Samara">Samara (GMT+4)</option>
              <option value="Europe/San_Marino">San Marino (GMT+1)</option>
              <option value="Europe/Sarajevo">Sarajevo (GMT+1)</option>
              <option value="Europe/Saratov">Saratov (GMT+4)</option>
              <option value="Europe/Simferopol">Simferopol (GMT+3)</option>
              <option value="Europe/Skopje">Skopje (GMT+1)</option>
              <option value="Europe/Sofia">Sofia (GMT+2)</option>
              <option value="Europe/Stockholm">Stockholm (GMT+1)</option>
              <option value="Europe/Tallinn">Tallinn (GMT+2)</option>
              <option value="Europe/Tirane">Tirane (GMT+1)</option>
              <option value="Europe/Ulyanovsk">Ulyanovsk (GMT+4)</option>
              <option value="Europe/Uzhgorod">Uzhgorod (GMT+2)</option>
              <option value="Europe/Vaduz">Vaduz (GMT+1)</option>
              <option value="Europe/Vatican">Vatican (GMT+1)</option>
              <option value="Europe/Vienna">Vienna (GMT+1)</option>
              <option value="Europe/Vilnius">Vilnius (GMT+2)</option>
              <option value="Europe/Volgograd">Volgograd (GMT+4)</option>
              <option value="Europe/Warsaw">Warsaw (GMT+1)</option>
              <option value="Europe/Zagreb">Zagreb (GMT+1)</option>
              <option value="Europe/Zaporozhye">Zaporozhye (GMT+2)</option>
              <option value="Europe/Zurich">Zurich (GMT+1)</option>
            </optgroup>
            
            <optgroup label="Indian">
              <option value="Indian/Antananarivo">Antananarivo (GMT+3)</option>
              <option value="Indian/Chagos">Chagos (GMT+6)</option>
              <option value="Indian/Christmas">Christmas (GMT+7)</option>
              <option value="Indian/Cocos">Cocos (GMT+6.5)</option>
              <option value="Indian/Comoro">Comoro (GMT+3)</option>
              <option value="Indian/Kerguelen">Kerguelen (GMT+5)</option>
              <option value="Indian/Mahe">Mahe (GMT+4)</option>
              <option value="Indian/Maldives">Maldives (GMT+5)</option>
              <option value="Indian/Mauritius">Mauritius (GMT+4)</option>
              <option value="Indian/Mayotte">Mayotte (GMT+3)</option>
              <option value="Indian/Reunion">Reunion (GMT+4)</option>
            </optgroup>
            
            <optgroup label="Pacific">
              <option value="Pacific/Apia">Apia (GMT+13)</option>
              <option value="Pacific/Auckland">Auckland (GMT+12)</option>
              <option value="Pacific/Bougainville">Bougainville (GMT+11)</option>
              <option value="Pacific/Chatham">Chatham (GMT+12.75)</option>
              <option value="Pacific/Chuuk">Chuuk (GMT+10)</option>
              <option value="Pacific/Easter">Easter (GMT-6)</option>
              <option value="Pacific/Efate">Efate (GMT+11)</option>
              <option value="Pacific/Enderbury">Enderbury (GMT+13)</option>
              <option value="Pacific/Fakaofo">Fakaofo (GMT+13)</option>
              <option value="Pacific/Fiji">Fiji (GMT+12)</option>
              <option value="Pacific/Funafuti">Funafuti (GMT+12)</option>
              <option value="Pacific/Galapagos">Galapagos (GMT-6)</option>
              <option value="Pacific/Gambier">Gambier (GMT-9)</option>
              <option value="Pacific/Guadalcanal">Guadalcanal (GMT+11)</option>
              <option value="Pacific/Guam">Guam (GMT+10)</option>
              <option value="Pacific/Honolulu">Honolulu (GMT-10)</option>
              <option value="Pacific/Kiritimati">Kiritimati (GMT+14)</option>
              <option value="Pacific/Kosrae">Kosrae (GMT+11)</option>
              <option value="Pacific/Kwajalein">Kwajalein (GMT+12)</option>
              <option value="Pacific/Majuro">Majuro (GMT+12)</option>
              <option value="Pacific/Marquesas">Marquesas (GMT-9.5)</option>
              <option value="Pacific/Midway">Midway (GMT-11)</option>
              <option value="Pacific/Nauru">Nauru (GMT+12)</option>
              <option value="Pacific/Niue">Niue (GMT-11)</option>
              <option value="Pacific/Norfolk">Norfolk (GMT+11)</option>
              <option value="Pacific/Noumea">Noumea (GMT+11)</option>
              <option value="Pacific/Pago_Pago">Pago Pago (GMT-11)</option>
              <option value="Pacific/Palau">Palau (GMT+9)</option>
              <option value="Pacific/Pitcairn">Pitcairn (GMT-8)</option>
              <option value="Pacific/Pohnpei">Pohnpei (GMT+11)</option>
              <option value="Pacific/Port_Moresby">Port Moresby (GMT+10)</option>
              <option value="Pacific/Rarotonga">Rarotonga (GMT-10)</option>
              <option value="Pacific/Saipan">Saipan (GMT+10)</option>
              <option value="Pacific/Tahiti">Tahiti (GMT-10)</option>
              <option value="Pacific/Tarawa">Tarawa (GMT+12)</option>
              <option value="Pacific/Tongatapu">Tongatapu (GMT+13)</option>
              <option value="Pacific/Wake">Wake (GMT+12)</option>
              <option value="Pacific/Wallis">Wallis (GMT+12)</option>
            </optgroup>
            </Select>
          </SettingRow>
          <SettingRow>
            <SettingInfo>
              <SettingLabel>Date Format</SettingLabel>
              <SettingDescription>How dates are displayed</SettingDescription>
            </SettingInfo>
            <Select
              value={localSettings.preferences.dateFormat}
              onChange={(e) => handleChange('preferences', 'dateFormat', e.target.value)}
            >
              <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2025)</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2025)</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD (2025-12-31)</option>
            </Select>
          </SettingRow>
          <SettingRow>
            <SettingInfo>
              <SettingLabel>Time Format</SettingLabel>
              <SettingDescription>12-hour or 24-hour clock</SettingDescription>
            </SettingInfo>
            <Select
              value={localSettings.preferences.timeFormat}
              onChange={(e) => handleChange('preferences', 'timeFormat', e.target.value)}
            >
              <option value="12h">12-hour (3:00 PM)</option>
              <option value="24h">24-hour (15:00)</option>
            </Select>
          </SettingRow>
          <SettingRow last>
            <SettingInfo>
              <SettingLabel>Week Start</SettingLabel>
              <SettingDescription>First day of the week in calendar</SettingDescription>
            </SettingInfo>
            <Select
              value={localSettings.preferences.weekStart}
              onChange={(e) => handleChange('preferences', 'weekStart', e.target.value)}
            >
              <option value="sunday">Sunday</option>
              <option value="monday">Monday</option>
            </Select>
          </SettingRow>
        </SettingCard>
      </Section>

      <Section>
        <SectionTitle>Display</SectionTitle>
        <SettingCard compact={isCompact}>
          <SettingRow>
            <SettingInfo>
              <SettingLabel>Semester Label</SettingLabel>
              <SettingDescription>Choose whether the middle academic term is called Spring or Winter</SettingDescription>
            </SettingInfo>
            <Select
              value={localSettings.preferences.semesterMiddleLabel || 'spring'}
              onChange={(e) => handleChange('preferences', 'semesterMiddleLabel', e.target.value)}
            >
              <option value="spring">Spring</option>
              <option value="winter">Winter</option>
            </Select>
          </SettingRow>
          <SettingRow>
            <SettingInfo>
              <SettingLabel>Theme</SettingLabel>
              <SettingDescription>Choose between light and dark theme</SettingDescription>
            </SettingInfo>
            <Select
              value={localSettings.theme || 'dark'}
              onChange={(e) => handleChange(null, 'theme', e.target.value)}
            >
              <option value="dark">Dark</option>
              <option value="light">Light</option>
            </Select>
          </SettingRow>
          <SettingRow>
            <SettingInfo>
              <SettingLabel>Super Compact Mode</SettingLabel>
              <SettingDescription>Significantly reduce spacing and padding for more content on screen</SettingDescription>
            </SettingInfo>
            <ToggleSwitch>
              <ToggleInput
                type="checkbox"
                checked={localSettings.display.compactMode}
                onChange={(e) => handleChange('display', 'compactMode', e.target.checked)}
              />
              <ToggleSlider />
            </ToggleSwitch>
          </SettingRow>
          <SettingRow>
            <SettingInfo>
              <SettingLabel>Show Active Assignments Only</SettingLabel>
              <SettingDescription>Filter to show only active (non-completed) assignments</SettingDescription>
            </SettingInfo>
            <ToggleSwitch>
              <ToggleInput
                type="checkbox"
                checked={localSettings.display.showCompletedAssignments}
                onChange={(e) => handleChange('display', 'showCompletedAssignments', e.target.checked)}
              />
              <ToggleSlider />
            </ToggleSwitch>
          </SettingRow>

          <SettingRow last>
            <SettingInfo>
              <SettingLabel>Show Upcoming Deadlines</SettingLabel>
              <SettingDescription>Show the upcoming deadlines sidebar on the calendar by default (can be toggled per session)</SettingDescription>
            </SettingInfo>
            <ToggleSwitch>
              <ToggleInput
                type="checkbox"
                checked={localSettings.display.showUpcomingDeadlines}
                onChange={(e) => handleChange('display', 'showUpcomingDeadlines', e.target.checked)}
              />
              <ToggleSlider />
            </ToggleSwitch>
          </SettingRow>
        </SettingCard>
      </Section>

      <Section>
        <SectionTitle>GPA Settings</SectionTitle>
        <SettingCard compact={isCompact}>
          <SettingDescription style={{ marginBottom: '1rem', fontSize: '0.875rem' }}>
            Customize GPA values and percentage ranges for each letter grade. Changes will affect average grade calculations.
          </SettingDescription>
          {Object.entries(localSettings.gpa).map(([grade, config]) => (
            <SettingRow key={grade} style={{ borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem', marginBottom: '0.5rem' }}>
              <SettingInfo>
                <SettingLabel>{grade} Grade</SettingLabel>
                <SettingDescription>Min: {config.min}%, Max: {config.max}%, GPA: {config.value}</SettingDescription>
              </SettingInfo>
              <GPAInputContainer>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                  <SmallLabel>Min %</SmallLabel>
                  <Input
                    type="number"
                    min="0"
                      max="100"
                      value={config.min}
                      onChange={(e) => handleGPAChange(grade, 'min', parseInt(e.target.value))}
                      style={{ width: '60px', fontSize: '0.75rem' }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                    <SmallLabel>Max %</SmallLabel>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={config.max}
                      onChange={(e) => handleGPAChange(grade, 'max', parseInt(e.target.value))}
                      style={{ width: '60px', fontSize: '0.75rem' }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                    <SmallLabel>GPA</SmallLabel>
                    <Input
                      type="number"
                      min="0"
                      max="4"
                      step="0.01"
                      value={config.value}
                      onChange={(e) => handleGPAChange(grade, 'value', parseFloat(e.target.value))}
                      style={{ width: '60px', fontSize: '0.75rem' }}
                    />
                  </div>
                </GPAInputContainer>
              </SettingRow>
            ))}
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
              <ElevatedButton
                onClick={() => {
                  const defaultGPA = {
                    A: { min: 93, max: 100, value: 4.0 },
                    'A-': { min: 90, max: 92, value: 3.67 },
                    'B+': { min: 87, max: 89, value: 3.33 },
                    B: { min: 83, max: 86, value: 3.0 },
                    'B-': { min: 80, max: 82, value: 2.67 },
                    'C+': { min: 77, max: 79, value: 2.33 },
                    C: { min: 73, max: 76, value: 2.0 },
                    'C-': { min: 70, max: 72, value: 1.67 },
                    'D+': { min: 67, max: 69, value: 1.33 },
                    D: { min: 60, max: 66, value: 1.0 },
                    F: { min: 0, max: 59, value: 0.0 }
                  };
                  setLocalSettings(prev => ({
                    ...prev,
                    gpa: defaultGPA
                  }));
                }}
                style={{
                  background: '#6c757d',
                  border: '1px solid #6c757d',
                  fontSize: '0.875rem',
                  padding: '0.5rem 1rem'
                }}
              >
                Reset to Defaults
              </ElevatedButton>
            </div>
          </SettingCard>
        </Section>      <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '2rem' }}>
        <SaveButton onClick={handleSave} disabled={!hasChanges}>
          Save Settings
        </SaveButton>
        {saveStatus && (
          <SaveIndicator success={saveStatus === 'success'} error={saveStatus === 'error'}>
            {saveStatus === 'success' ? 'Settings saved successfully!' : 
             saveStatus === 'error' ? 'Failed to save settings. Please try again.' : 'Saving...'}
          </SaveIndicator>
        )}
        {hasChanges && !saveStatus && (
          <span style={{ marginLeft: '1rem', color: '#fbc02d', fontSize: '0.875rem' }}>
            You have unsaved changes
          </span>
        )}
      </div>
    </Container>
  );
};

export default SettingsForm;